import { Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonService } from '../../../common/services/common.service';
import { Store } from '@ngrx/store';
import { getCurrentUser } from 'app/core/store/currentUser/user.selectors';
import { getLoggedInUser } from 'app/core/store/loggedInUser/loggedInUser.selectors';
import { getImmigrationDetails } from 'app/core/store/immigrationDetails/immigrationDetails.selector';
import { ConsultantModel } from '../../../common/models/consultant.model';
import { ConsultantDetailsService } from '../../../common/services/consultant-details.service';
import { ProjectTaskService } from '../../../common/services/project-task.service';
import { ProjectTaskModel } from '../../../common/models/project-task.model';
import { ConsultantRateService } from '../../../common/services/consultant-rate.service';
import { ConsultantRateModel, ConsultantProjectRateParameterModel } from '../../../common/models/consultant-rate.model';
import { PlanEntryService } from '../../../common/services/plan-entry.service';
import { SnackbarService } from '../../../common/services/snackbar.service';
import { Constants } from '../../../common/utilities/constants.config';
import { ProjectsService } from '../../../common/services/projects.service';
import { firstValueFrom } from 'rxjs';
import { TimeEntryForPlanningService } from '../time-entry-for-planning.service';

// Recurrence settings for a plan entry that repeats (daily or weekly)
export interface PlanRecurrence {
  pattern: 'Daily' | 'Weekly';
  dailyMode?: 'Interval' | 'Weekday';
  dailyInterval?: number;
  weeklyInterval?: number;
  weekdays?: string[];
  occurrences: number;
  rangeMode?: 'occurrences' | 'endBy'; 
  endByDate?: string | null; 
}

// A single plan entry row, including recurrence and clash metadata
export interface PlanEntry {
  id: number;
  date: string;
  client: string;
  consultant: string;
  entryOwner: string;
  entryOwnerLoginName?: string;
  entryOwnerIsAdmin?: boolean | null;
  company: string;
  companyId?: number | null;
  project: string;
  taskRef: string;
  task: string;
  hours: number;
  rate: number;
  allocation: string;
  charge: 'Yes' | 'No';
  invoiceDate: string;
  isPlaceholder?: boolean;
  seriesId: string | null;
  recurrence?: PlanRecurrence;
  causedClash?: boolean;
  isUnsaved?: boolean;
  isClashEntry?: boolean;
  taskId?: number | null;
  currencyCode?: string | null;
}

// Result emitted back to the caller when a plan (or recurring series) is saved
export interface PlanDialogResult {
  entries: PlanEntry[];
  replacedSeriesId?: string;
  replacedEntryId?: number;
}

// Template used to restore the user's last-used field selections on the next new entry
export interface PlanSelectionTemplate {
  client: string | null;
  employee: string | null;
  company: string | null;
  companyId?: number | null;
  project: string | null;
  taskRef: string | null;
  task: string;
  hours: number;
  hourMode: string;
  rate: number | null;
  allocation: string | null;
  charge: 'Yes' | 'No' | null;
}
// Cached currency info shape returned by CommonService.getProjectCurrencyCode
interface ProjectCurrencyInfo {
  currencyCode: string | null;
  currencySymbol: string | null;
}

interface CalendarCell {
  day: number;
  iso: string;
  inMonth: boolean;
  highlighted: boolean;
}

interface CalendarMonth {
  label: string;
  cells: CalendarCell[];
} 

@Component({
  selector: 'app-add-plan-dialog',
  templateUrl: './add-plan-dialog.component.html',
  styleUrls: ['./add-plan-dialog.component.scss']
})
export class AddPlanDialogComponent implements OnInit, OnChanges {
  @Input() data: PlanEntry | null = null;
  @Output() result = new EventEmitter<PlanDialogResult>();
  @Output() cancelled = new EventEmitter<void>();

  // localStorage key used to persist the user's last field selections across dialog opens
  private readonly SELECTION_STORAGE_KEY = 'mips.addPlanDialog.savedSelection';

  // Cache of project currency lookups keyed by "client|project" so we don't refetch
  // the same project's currency code/symbol on every rate reload (date/employee changes, etc.)
  private projectCurrencyCache = new Map<string, ProjectCurrencyInfo>();

  selectedClient: any;
  clients: any[] = [];
  employees: any[] = [];
  companies: any[] = [];
  projects: any[] = [];
  taskRefs: ProjectTaskModel[] = [];
  rates: ConsultantRateModel[] = [];
  hourModes = ['Hours', 'Half Day', 'Full Day'];

  // Maps day abbreviation to JS Date.getDay() index
  weekdayMap: { [key: string]: number } = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6
  };

  // Working weekdays only (Mon–Fri) for the weekly recurrence day picker
  weekdays = Object.keys(this.weekdayMap).filter(
    day => day !== 'Sun' && day !== 'Sat'
  );

  selectedWeekdays: string[] = [];

  // currentUser reflects the consultant the session is currently acting as.
  currentUser: ConsultantModel;
  currentUserName = '';
  authenticatedUserName = '';
  private authenticatedUser: any = null;
  loggedInUsername = '';
  consultantImmigrationDetails: any;
  consultantWorkCountry: string | null = null;
  consultantWorkHour: number | null = null;
  invoiceOnSameDate = true;
  isEditMode = false;
  editingSeries = false;
  saveSelection = true;
  recurrenceExpanded = false;
  ratesLoading = false;
    private rateChosenByUser = false;
activeDatepickerField: 'entry' | 'invoice' | null = null;            
  private lastWeekendWarningAt = 0;

  form: FormGroup;

  /**
   * Constructor
   */
  constructor(
    private formBuilder: FormBuilder,
    private _store: Store,
    private commonService: CommonService,
    private consultantDetailsService: ConsultantDetailsService,
    private projectTaskService: ProjectTaskService,
    private consultantRateService: ConsultantRateService,
    private planEntryService: PlanEntryService,
    private cdr: ChangeDetectorRef,
    private _snackBarService: SnackbarService,
    private projectService: ProjectsService,
    private timeEntryForPlanningService: TimeEntryForPlanningService,
  ) {

        this._store.select(getCurrentUser).subscribe((user) => {
      this.currentUser = user;
      this.loggedInUsername = user?.LoginName ?? '';
      this.updateCurrentUserName();
    });

    this._store.select(getLoggedInUser).subscribe((user) => {
      this.authenticatedUser = user;
      this.updateAuthenticatedUserName();
    });

    // Used to display the consultant's work country/hours in the dialog header
    this._store.select(getImmigrationDetails).subscribe((immigrationDetails) => {
      this.consultantImmigrationDetails = immigrationDetails;
      this.consultantWorkCountry = this.consultantImmigrationDetails?.['consultantWorkCountry'] ?? null;
      this.consultantWorkHour = this.consultantImmigrationDetails?.['consultantWorkHour'] ?? null;
    });
  }
  // Resolves the display name for entryOwner from the Employee code, since impersonation
  // swaps user.Employee but not user.FirstName - matching against the consultant list
  // (like PlanFilterPanelComponent does for its employee filter) gets the impersonated
  // person's actual name instead of the real logged-in user's. Safe to call before
  // `employees` has loaded - it falls back to the raw FirstName until the list populates.
  private updateCurrentUserName(): void {
    const employeeCode = this.currentUser?.Employee;
    if (!employeeCode) {
      this.currentUserName = this.currentUser?.FirstName?.trim() ?? '';
      return;
    }

    const matched = this.employees.find(
      e => e.Employee?.trim().toLowerCase() === employeeCode.trim().toLowerCase()
    );

    this.currentUserName = matched
  ? matched.FirstName?.trim() ?? ''
  : this.currentUser?.FirstName?.trim() ?? '';
  }

  private updateAuthenticatedUserName(): void {
    const employeeCode = this.authenticatedUser?.userProfile?.employee;
    const loginName = this.authenticatedUser?.username;
    const matched = this.employees.find(e =>
      (employeeCode && e.Employee?.trim().toLowerCase() === employeeCode.trim().toLowerCase()) ||
      (loginName && e.LoginName?.trim().toLowerCase() === loginName.trim().toLowerCase())
    );

    this.authenticatedUserName = matched?.FullName ?? '';
  }

  // ----- Lifecycle hooks -----

  /**
   * On init
   */
  async ngOnInit(): Promise<void> {
    this.buildForm();
    await this.loadClients();
    await this.loadConsultants();
    this.updateCurrentUserName(); // re-resolve now that `employees` is populated
    this.updateAuthenticatedUserName();
    await this.prefillDependentDropdowns();

   // Client selection -> reset the project and Charge fields, reload projects,
  // and refresh consultant rates for the selected client.
    this.form.get('client')!.valueChanges.subscribe(async (clientCode) => {
    this.form.get('project')!.setValue(null);
    this.form.get('charge')!.setValue(null);

    if (clientCode) {
      await this.loadProjects(clientCode);
    } else {
      this.projects = [];
    }

    this.loadConsultantRates();
  });

    // Employee -> set company + company ID + reload rates
    this.form.get('employee')!.valueChanges.subscribe((employeeCode: string) => {
      if (!employeeCode) {
        this.form.get('companyId')?.setValue(null, { emitEvent: false });
        this.loadConsultantRates();
        return;
      }

      const selectedEmployee = this.employees.find(e => e.Employee === employeeCode);

      if (selectedEmployee?.Company_Name) {
        this.form.get('company')?.setValue(selectedEmployee.Company_Name, { emitEvent: false });
      }

      this.loadConsultantCompany(employeeCode);
      this.loadConsultantRates();
    });

    // Project -> reset taskRef, reset rate, reload tasks + rates
    this.form.get('project')!.valueChanges.subscribe((projectCode: string) => {
      this.form.get('taskRef')!.setValue(null);
      this.form.get('taskId')!.setValue(null);
      this.form.get('rate')!.setValue(null, { emitEvent: false });
      this.rateChosenByUser = false;
      this.form.get('currencyCode')!.setValue(null, { emitEvent: false });

      // Defaults Charge from the selected project's Charge property.
      const selectedProject = this.projects.find(
        project =>
          String(project.Project).trim().toLowerCase() === String(this.form.get('project')?.value ?? '').trim().toLowerCase()
      );

      if (selectedProject) {
        const isChargeable =String(selectedProject.Charge ?? '').toLowerCase() === 'yes';
        this.form.get('charge')?.setValue(isChargeable ? 'Yes' : 'No',{ emitEvent: false } );
      }

      const clientCode = this.form.get('client')!.value;

      if (clientCode && projectCode) {
        this.loadProjectTasks(clientCode, projectCode, true);
        this.loadConsultantRates();
      } else {
        this.taskRefs = [];
        this.rates = [];
        this.ratesLoading = false;
      }
    });

    // Task ref -> capture the real numeric Task_id
    this.form.get('taskRef')!.valueChanges.subscribe((taskRefValue: string) => {
      const selectedTask = this.taskRefs.find(
        t => t.Task_name === taskRefValue || t.Display_String === taskRefValue
      );

      this.form.get('taskId')!.setValue(selectedTask?.Task_id ?? null, { emitEvent: false });
    });

    // Rate -> capture the matching currency code
    this.form.get('rate')!.valueChanges.subscribe((rateValue: any) => {
      this.rateChosenByUser = true;
      this.form.get('currencyCode')!.setValue(
        rateValue?.Converted_Currency_Code ?? rateValue?.Currency_Code ?? null
      );
    });

    // Date -> reload rates
    this.form.get('date')!.valueChanges.subscribe(() => {
      this.loadConsultantRates();
    });
  }

  /**
   * On changes - rebuilds the form when the dialog is reopened with different data
   * (e.g. switching between edit entries, or from edit back to a blank new entry)
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.buildForm();
      this.prefillDependentDropdowns();
    }
  }

  // Repopulates dependent dropdowns (company, projects, tasks, rates) so they display
  // correctly when the form is seeded from an existing entry or a saved selection
  private async prefillDependentDropdowns(): Promise<void> {
    const value = this.form.getRawValue();

    const tasks: Promise<void>[] = [];

    // Employee -> load company ID for the initial form value
    if (value.employee) {
      tasks.push(
        this.loadConsultantCompany(value.employee).then(() => {
          // Also resolve the company NAME so the ng-select displays correctly
          const selectedEmployee = this.employees.find(e => e.Employee === value.employee);

          if (selectedEmployee?.Company_Name) {
            this.form.get('company')?.setValue(selectedEmployee.Company_Name, { emitEvent: false });
          }
        })
      );
    }

    if (value.client) {
      tasks.push(this.loadProjects(value.client));
    }

    if (value.client && value.project) {
      tasks.push(
        this.loadProjectTasks(value.client, value.project).then(() => {
          this.loadConsultantRates();
        })
      );
    }
 
    await Promise.all(tasks);

    const taskRefValue = this.form.get('taskRef')?.value;
    if (taskRefValue) {
      const selectedTask = this.taskRefs.find(
        t => t.Task_name === taskRefValue || t.Display_String === taskRefValue
      );
      this.form.get('taskId')?.setValue(selectedTask?.Task_id ?? null, { emitEvent: false });
    }
  }

  // Fetches the list of clients for the Client dropdown
  private async loadClients(): Promise<void> {
    try {
      this.clients = await this.commonService.getAllClients('addPlanDialog');
    } catch (err) {
      console.error('Failed to load clients', err);
    }
  }

  // Resolves the numeric company ID for a given employee, needed when the entry is saved
  private loadConsultantCompany(employeeCode: string): Promise<void> {
    return new Promise((resolve) => {
      if (!employeeCode) {
        this.form.get('companyId')?.setValue(null, { emitEvent: false });
        resolve();
        return;
      }

      this.planEntryService.getConsultantCompany(employeeCode).subscribe({
        next: (response: any) => {
          const companyId = Number(response?.company_id);

          this.form.get('companyId')?.setValue(
            Number.isFinite(companyId) ? companyId : null,
            { emitEvent: false }
          );

          resolve();
        },

        error: (err) => {
          console.error('Failed to get company ID:', err);
          this.form.get('companyId')?.setValue(null, { emitEvent: false });
          resolve();
        }
      });
    });
  }
 

  // Formats a rate for display: prefers a currency symbol prefix ("$8.00"), falls back
  // to a trailing currency code ("8.00 USD"), falls back to a bare number ("8.00").
  private formatRate(rate: number, currencyCode?: string | null, currencySymbol?: string | null): string {
    const formattedRate = Number(rate).toFixed(2);

    if (currencySymbol) {
      return `${currencySymbol}${formattedRate}`;
    }

    if (currencyCode) {
      return `${formattedRate} ${currencyCode}`;
    }

    return formattedRate;
  }

  private rateOptionKey(rate: any, fallbackCurrencyCode?: string | null): string | null {
    const rateValue = rate !== null && typeof rate === 'object'
      ? (rate.Converted_Rate ?? rate.Rate)
      : rate;
    const currencyCode = rate !== null && typeof rate === 'object'
      ? (rate.Converted_Currency_Code ?? rate.Currency_Code ?? fallbackCurrencyCode)
      : fallbackCurrencyCode;

    if (rateValue === null || rateValue === undefined || rateValue === '' || !currencyCode) {
      return null;
    }

    const numericRate = Number(rateValue);
    return Number.isFinite(numericRate)
      ? `${String(currencyCode).trim().toUpperCase()}|${numericRate}`
      : null;
  }

  // Resolves a project's currency code + symbol, using a per-session cache keyed by
  // "client|project" so repeated rate reloads (date/employee changes, etc.) don't
  // refetch the same project's currency info from the server every time.
  private async resolveProjectCurrency(client: string, project: string): Promise<ProjectCurrencyInfo> {
    const cacheKey = `${client}|${project}`;
    const cached = this.projectCurrencyCache.get(cacheKey);

    if (cached) {
      return cached;
    }

    let info: ProjectCurrencyInfo = { currencyCode: null, currencySymbol: null };

    try {
      const projectCurrency = await this.commonService.getProjectCurrencyCode(client, project);
      info = {
        currencyCode: projectCurrency.currencyCode ?? null,
        currencySymbol: projectCurrency.currencySymbol ?? null
      };
    } catch (err) {
      console.error('Failed to load project currency code', err);
    }

    this.projectCurrencyCache.set(cacheKey, info);
    return info;
  }

   // A 0.00 option in the project's currency always comes first, followed by the project rates
  // and the consultant "(base):" rows. Default selection: First consultant rate for the project; else the
  // Base rate of the Project ; else 0.00.
  private loadConsultantRates(): void {
    const value = this.form.getRawValue();

    if (!value.client || !value.project || !value.employee || !value.date) {
      this.rates = [];
      this.ratesLoading = false;
      return;
    }

    const params: ConsultantProjectRateParameterModel = {
      client: value.client,
      project: value.project,
      employee: value.employee,
      entrydate: this.normalizeDate(value.date).slice(0, 7).replace('-', '')
    };

    this.ratesLoading = true;

    this.consultantRateService.getConsultantByProject(params).subscribe({
      next: async (rates: ConsultantRateModel[]) => {
        const currentRateControlValue = this.form.get('rate')?.value;
        const currentRate = currentRateControlValue?.Converted_Rate ?? currentRateControlValue;
        const currentCurrencyCode = currentRateControlValue?.Converted_Currency_Code ??
          currentRateControlValue?.Currency_Code ?? this.form.get('currencyCode')?.value;

        let effectiveRates: ConsultantRateModel[] = rates ? [...rates] : [];
        const { currencyCode: projectCurrencyCode, currencySymbol: projectCurrencySymbol } =
          await this.resolveProjectCurrency(value.client, value.project);
        const fallbackCurrencyCode = projectCurrencyCode ?? currentCurrencyCode ?? null;
        const zeroRateKey = this.rateOptionKey(0, fallbackCurrencyCode);
        const zeroRateIndex = zeroRateKey === null ? -1 : effectiveRates.findIndex(rate =>
          Number(rate.Converted_Rate) === 0 &&
          this.rateOptionKey(rate) === zeroRateKey &&
          !rate.Is_Default_Rate
        );
        if (zeroRateIndex >= 0) {
          const [zeroRate] = effectiveRates.splice(zeroRateIndex, 1);
          effectiveRates.unshift(zeroRate);
        } else {
          const fallbackRate: ConsultantRateModel = {
            Rate: '0.000',
            Currency_Code: fallbackCurrencyCode,
            Currency_symbol_short: projectCurrencySymbol,
            Converted_Rate: '0.000',
            Converted_Currency_Code: fallbackCurrencyCode,
            Converted_Currency_Symbol: projectCurrencySymbol,
            Is_Default_Rate: 0,
            Is_Converted_Rate: 0,
            Client: value.client,
            Project: value.project,
            Display_Rate: this.formatRate(0, fallbackCurrencyCode, projectCurrencySymbol)
          } as unknown as ConsultantRateModel;
          effectiveRates.unshift(fallbackRate);
        }

        this.rates = effectiveRates;

        const currentRateKey = this.rateOptionKey(currentRateControlValue, currentCurrencyCode);
        const hasSavedOrSelectedRate = currentRateControlValue !== null &&
          currentRateControlValue !== undefined && currentRateControlValue !== '' &&
          (this.rateChosenByUser || this.isEditMode);
        const matchingCurrentRate = currentRateKey
          ? effectiveRates.find(rate => this.rateOptionKey(rate) === currentRateKey)
          : undefined;
        const selectedRate = hasSavedOrSelectedRate
          ? (
              matchingCurrentRate
              ?? effectiveRates.find(
                rate => this.rateOptionKey(rate) === currentRateKey
              )
              ?? effectiveRates[0]
            )
          : (
              effectiveRates.find(
                rate => !rate.Is_Default_Rate && Number(rate.Converted_Rate) !== 0
              )
              ?? effectiveRates.find(rate => !!rate.Is_Project_Country_Base)
              ?? effectiveRates[0]
            );

        setTimeout(() => {
          this.form.get('rate')?.setValue(selectedRate, { emitEvent: false });

          const currencyCode = selectedRate.Converted_Currency_Code ?? selectedRate.Currency_Code ?? null;

          this.form.get('currencyCode')?.setValue(currencyCode, { emitEvent: false });

          this.cdr.detectChanges();
        });

        this.ratesLoading = false;

      },

      error: (err) => {
        console.error('Failed to load consultant rates', err);

        this.rates = [];

        this.form.get('rate')?.setValue(null, { emitEvent: false });
        this.form.get('currencyCode')?.setValue(null, { emitEvent: false });

        this.ratesLoading = false;
      }
    });
  }

  // Fetches all consultants and derives the distinct list of companies from them
  private loadConsultants(): Promise<void> {
    return new Promise((resolve) => {
      this.consultantDetailsService.getConsultantDetails().subscribe({
        next: (consultants: any[]) => {
          this.employees = consultants.map(c => ({
            ...c,
            FullName: `${c.FirstName} ${c.LastName}`.trim()
          }));

          const uniqueCompanyNames = Array.from(
            new Set(this.employees.map(e => e.Company_Name).filter(Boolean))
          );
          this.companies = uniqueCompanyNames.map(name => ({ Company_Name: name }));

          resolve();
        },
        error: (err) => {
          console.error('Failed to load consultants', err);
          resolve(); // resolve even on error so init doesn't hang
        }
      });
    });
  }

  /**
   * Loads projects available to the current employee and filters them
   * by the selected client for the Project dropdown.
   */
  private async loadProjects(client: string): Promise<void> {
  try {
    const employee = this.currentUser?.Employee;

    if (!employee) {
      this.projects = [];
      return;
    }

    // Get the selected Plan period.
    const dateRange = this.timeEntryForPlanningService.getDateRange();
    const startDate = dateRange.start;
    const endDate = dateRange.end;

    if (!startDate || !endDate) {
      this.projects = [];
      return;
    }

    // Fetch the employee's projects for the selected Plan period.
    const projectList = await firstValueFrom(
      this.projectService.getProjects(
        false,
        employee,
        startDate,
        endDate
      )
    );

    // Show only projects belonging to the selected client, with a
    // "Description - Project" label for the dropdown.
    this.projects = projectList
      .filter(
        project =>
          String(project.Client).trim().toLowerCase() ===
          String(client).trim().toLowerCase()
      )
      .map(project => ({
        ...project,
        Project_Label: `${project.Description} - ${project.Project}`
      }));

    this.cdr.detectChanges();
  } catch (err) {
    console.error('Failed to load projects', err);
    this.projects = [];
  }
}

  // Fetches task references for the given client/project
  private loadProjectTasks(
    client: string,
    project: string,
    forceReload: boolean = false
  ): Promise<void> {
    return new Promise((resolve) => {
      this.projectTaskService.getProjectTasks(client, project, forceReload).subscribe({
        next: (tasks: ProjectTaskModel[]) => {
          this.taskRefs = tasks;
          resolve();
        },
        error: (err) => {
          console.error('Failed to load project tasks', err);
          this.taskRefs = [];
          resolve();
        }
      });
    });
  }

  // (Re)builds the reactive form, seeding values from an existing entry (edit mode)
  // or from a saved selection template (new entry) when available
  private buildForm(): void {
    this.isEditMode = !!this.data;
    this.editingSeries = !!this.data?.seriesId;
    this.rateChosenByUser = false;

    this.invoiceOnSameDate = this.data
      ? this.data.date === this.data.invoiceDate
      : true;

    const today = this.getTodayAsISOString();
    const recurrence = this.data?.recurrence;

    this.selectedWeekdays = recurrence?.weekdays ? [...recurrence.weekdays] : [];
    this.recurrenceExpanded = this.editingSeries;

    const savedSelection: PlanSelectionTemplate | null = this.isEditMode
      ? null
      : this.loadSelectionTemplate();

    this.form = this.formBuilder.group({
      client: [this.data?.client ?? savedSelection?.client ?? null, Validators.required],
      employee: [this.data?.consultant ?? savedSelection?.employee ?? null, Validators.required],
      company: [this.data?.company ?? savedSelection?.company ?? null, Validators.required],
      companyId: [this.data?.companyId ?? null],
      project: [this.data?.project ?? savedSelection?.project ?? null, Validators.required],
      taskRef: [this.data?.taskRef ?? savedSelection?.taskRef ?? null],
      task: [this.data?.task ?? savedSelection?.task ?? '', Validators.required],
      date: [this.data?.date ?? today, Validators.required],
      hours: [
        this.data?.hours ?? savedSelection?.hours ?? 8,
        [Validators.required, Validators.min(0.5)]
      ],
      hourMode: [savedSelection?.hourMode ?? 'Hours'],
      rate: [this.data?.rate ?? savedSelection?.rate ?? null, Validators.required],
      allocation: [
        this.data?.allocation || savedSelection?.allocation || 'Full',
        Validators.required
      ],
      charge: [this.data?.charge ?? savedSelection?.charge ?? null, Validators.required],
      invoiceDate: [this.data?.invoiceDate ?? today],
      repeatEntry: [this.editingSeries],
      recurrencePattern: [recurrence?.pattern ?? 'Daily'],
      dailyMode: [recurrence?.dailyMode ?? 'Interval'],
      dailyInterval: [recurrence?.dailyInterval ?? 1, [Validators.min(1)]],
      weeklyInterval: [recurrence?.weeklyInterval ?? 1, [Validators.min(1)]],
      occurrences: [
        recurrence?.occurrences ?? 10,
        [Validators.required, Validators.min(1)]
      ],
      rangeMode: [recurrence?.rangeMode ?? 'occurrences'], 
      endByDate: [recurrence?.endByDate ?? today],  
      taskId: [this.data?.taskId ?? null],
      currencyCode: [this.data?.currencyCode ?? null]
    });

    this.onDailyModeChange();
    this.onRangeModeChange(); 
  }

  // Returns today's date as a YYYY-MM-DD string
  private getTodayAsISOString(): string {
    return this.toISO(new Date());
  }

  compareRates = (a: any, b: any): boolean => {
    if (a === b) {
      return true;
    }
    if (a === null || a === undefined || b === null || b === undefined) {
      return false;
    }

    const currencyCode = this.form?.get('currencyCode')?.value;
    const aKey = this.rateOptionKey(a, currencyCode);
    const bKey = this.rateOptionKey(b, currencyCode);
    return aKey !== null && aKey === bKey;
  };
 
  /**
   * Checks whether the selected project is non-chargeable.
   * Used to disable the "Yes" option when the project's Charge
   * property is "no".
   */
  get isProjectNonChargeable(): boolean {
    const projectCode = this.form.get('project')?.value;

    const selectedProject = this.projects.find(
      project => project.Project === projectCode
    );

    return (
      String(selectedProject?.Charge ?? '').toLowerCase() === 'no'
    );
  }

// Whether the form is valid and ready to submit - weekly recurrence additionally
// requires at least one weekday to be selected. In edit mode, also require the
// form to be dirty (something was actually changed) - otherwise Apply stays
// enabled on an unmodified entry and would trigger a pointless save.
get canSubmit(): boolean {
  if (this.form.invalid) {
    return false;
  }

  if (this.isEditMode && this.form.pristine) {
    return false;
  }

    const value = this.form.getRawValue();

  if (value.repeatEntry && value.rangeMode === 'endBy') {
    if (!value.endByDate || this.isEndByBeforeStart) {
      return false;
    }
  }

  if (value.repeatEntry && value.recurrencePattern === 'Weekly') {
    return this.selectedWeekdays.length > 0;
  }

  return true;
}

  // True when "End by" is chosen and its date is before the entry date
  get isEndByBeforeStart(): boolean {
    const value = this.form?.getRawValue();

    if (!value?.repeatEntry || value.rangeMode !== 'endBy' || !value.endByDate) {
      return false;
    }

    return this.normalizeDate(value.endByDate) < this.normalizeDate(value.date);
  }

  // Formats a Date object as YYYY-MM-DD using local date parts (avoids UTC timezone shifting)
  private toISO(d: Date): string {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Normalizes a date value (string, moment-like object, or Date) into a YYYY-MM-DD string
  private normalizeDate(value: any): string {
    if (!value) {
      return this.getTodayAsISOString();
    }

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value.format === 'function') {
      return value.format('YYYY-MM-DD');
    }

    return this.toISO(new Date(value));
  }

  // Applies preset hour values when Half Day / Full Day is selected
  setHoursForMode(): void {
    const mode = this.form.controls.hourMode.value;

    if (mode === 'Half Day') {
      this.form.controls.hours.setValue(4);
    }

    if (mode === 'Full Day') {
      this.form.controls.hours.setValue(8);
    }
  }

  // Expands/collapses the recurrence accordion and syncs the repeatEntry form control
  toggleRecurrenceExpanded(): void {
    this.recurrenceExpanded = !this.recurrenceExpanded;
    this.form.controls.repeatEntry.setValue(this.recurrenceExpanded);
    this.onRepeatToggle();
  }

  // Re-applies the daily-interval enable/disable state whenever recurrence is toggled on
  onRepeatToggle(): void {
    if (this.form.controls.repeatEntry.value) {
      this.onDailyModeChange();
    }
  }

  // Used by [matDatepickerFilter] to disable Saturday/Sunday in the date picker UI
  weekdayFilter = (date: any | null): boolean => {
    if (!date) {
      return true;
    }

    const day = typeof date.day === 'function'
      ? date.day()              
      : new Date(date).getDay(); 

    return day !== 0 && day !== 6;
  };
  
  onDatepickerOpened(field: 'entry' | 'invoice'): void {
    this.activeDatepickerField = field;
  }

  onDatepickerClosed(): void {
    this.activeDatepickerField = null;
  }

  @HostListener('document:click', ['$event'])
onDocumentClick(event: MouseEvent): void {
  if (!this.activeDatepickerField) {
    return;
  }

  const target = event.target as HTMLElement | null;
  if (!target) {
    return;
  }

  const disabledCell = target.closest('.mat-calendar-body-disabled');
  if (!disabledCell) {
    return;
  }

  this.warnWeekendBlocked(this.activeDatepickerField);
}

private warnWeekendBlocked(field: 'entry' | 'invoice'): void {
  const now = Date.now();
  if (now - this.lastWeekendWarningAt < Constants.SNACKBAR_TIME) {
    return;
  }
  this.lastWeekendWarningAt = now;

  const message = field === 'entry'
    ? Constants.SNACKBAR_MSG_PLAN_ENTRY_WEEKEND_DATE_BLOCKED
    : Constants.SNACKBAR_MSG_INVOICE_WEEKEND_DATE_BLOCKED;

  this._snackBarService.dismissSnackBar();
  this._snackBarService.showSnackBar(message, 'OK', 'warning');
}

  // Enables the daily interval input only when the "Interval" daily mode is selected
  onDailyModeChange(): void {
    const dailyMode = this.form.controls.dailyMode.value;

    if (dailyMode === 'Interval') {
      this.form.controls.dailyInterval.enable({ emitEvent: false });
    } else {
      this.form.controls.dailyInterval.disable({ emitEvent: false });
    }
  }

    // Only the active range option is editable: "End after N occurrences" or "End by <date>"
  onRangeModeChange(): void {
    const mode = this.form.controls.rangeMode.value;

    if (mode === 'endBy') {
      this.form.controls.occurrences.disable({ emitEvent: false });
      this.form.controls.endByDate.enable({ emitEvent: false });
    } else {
      this.form.controls.occurrences.enable({ emitEvent: false });
      this.form.controls.endByDate.disable({ emitEvent: false });
    }
  }

  isWeekdaySelected(day: string): boolean {
    return this.selectedWeekdays.includes(day);
  }
  toggleWeekday(day: string): void {
    this.selectedWeekdays = this.isWeekdaySelected(day)
      ? this.selectedWeekdays.filter(d => d !== day)
      : [...this.selectedWeekdays, day];
  }

  // Generates the list of entry dates for the current recurrence settings (Daily = weekdays
  // only, Weekly = selected weekdays on the given interval). Capped at a 2-year hard stop as
  // a safety guard against runaway loops if occurrences is ever set unreasonably high.
    private generateRecurrenceDates(startDateStr: string): string[] {
    const value = this.form.getRawValue();
    const dates: string[] = [];

    const start = new Date(this.normalizeDate(startDateStr));
    const cursor = new Date(start);
    const guardStart = start.getTime();

    // "End by" mode runs until the end date; "End after" mode runs for N occurrences
    const isEndBy = value.rangeMode === 'endBy' && !!value.endByDate;
    const endIso = isEndBy ? this.normalizeDate(value.endByDate) : null;
    const maxOccurrences = isEndBy ? Number.MAX_SAFE_INTEGER : (Number(value.occurrences) || 0);
    const hardStopMs = 1000 * 60 * 60 * 24 * 730;

    // Compares as YYYY-MM-DD strings to avoid timezone issues
    const pastEnd = () => !!endIso && this.toISO(cursor) > endIso;

    if (value.recurrencePattern === 'Daily') {
      while (dates.length < maxOccurrences) {
        if (cursor.getTime() - guardStart > hardStopMs || pastEnd()) {
          break;
        }

        const day = cursor.getDay();

        if (day !== 0 && day !== 6) {
          dates.push(this.toISO(cursor));
        }

        cursor.setDate(cursor.getDate() + 1);
      }

      return dates;
    }

    const weeklyInterval = Math.max(1, Number(value.weeklyInterval) || 1);
    const selectedDayNumbers = this.selectedWeekdays.map(d => this.weekdayMap[d]);

    const startWeekBegin = new Date(start);
    startWeekBegin.setDate(start.getDate() - start.getDay());

    while (dates.length < maxOccurrences) {
      if (cursor.getTime() - guardStart > hardStopMs || pastEnd()) {
        break;
      }

      const day = cursor.getDay();

      if (selectedDayNumbers.includes(day)) {
        const weeksSinceStart = Math.floor(
          (cursor.getTime() - startWeekBegin.getTime()) / (1000 * 60 * 60 * 24 * 7)
        );

        if (weeksSinceStart % weeklyInterval === 0) {
          dates.push(this.toISO(cursor));
        }
      }

      cursor.setDate(cursor.getDate() + 1);
    }

    return dates;
  }

  // ----- Preview helpers -----

  get previewDates(): string[] {
    if (!this.form) {
      return [];
    }

    return this.generateRecurrenceDates(this.form.getRawValue().date);
  }

  // Human-readable summary shown in the accordion header (e.g. "3 days a week · 12 entries")
  get recurrenceSummary(): string {
    const value = this.form.getRawValue();
    const count = this.previewDates.length;

    if (value.recurrencePattern === 'Weekly') {
      return `${this.selectedWeekdays.length} days a week · ${count} entries`;
    }

    return `${count} entries`;
  }

  get previewTotalHours(): number {
    const hours = Number(this.form.getRawValue().hours) || 0;
    return this.previewDates.length * hours;
  }
   // Hourly rate currently selected in the Rate dropdown (0 if none)
  get previewRate(): number {
    const rate = this.form.getRawValue().rate;
    return Number(rate?.Converted_Rate ?? rate) || 0;
  }

 
  get previewCurrencyCode(): string {
    return this.form.getRawValue().currencyCode ?? '';
  }

  // Total cost of all entries the recurrence will create: rate x total hours
  get previewTotalCost(): number {
    return this.previewTotalHours * this.previewRate;
  }


  get previewDateRangeLabel(): string {
    const dates = this.previewDates;

    if (!dates.length) {
      return '-';
    }

    return `${this.formatDisplayDate(dates[0])} – ${this.formatDisplayDate(dates[dates.length - 1])}`;
  }

  // Formats an ISO date for display, e.g. "5 Jun 2026"
  private formatDisplayDate(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  // ----- Mini calendar -----

  // Cached so the grid isn't rebuilt on every change-detection pass
  private calendarCache: { key: string; months: CalendarMonth[] } | null = null;

  // One entry per month from the start date's month to the last generated entry's month
  get calendarMonths(): CalendarMonth[] {
    const raw = this.form.getRawValue();
    const startIso = this.normalizeDate(raw.date);

    const key = JSON.stringify([
      startIso,
      raw.recurrencePattern,
      raw.weeklyInterval,
      raw.rangeMode,
      raw.occurrences,
      raw.endByDate ? this.normalizeDate(raw.endByDate) : null,
      this.selectedWeekdays
    ]);

    if (this.calendarCache?.key === key) {
      return this.calendarCache.months;
    }

    const dates = this.previewDates;
    const highlightedDates = new Set(dates);

    const start = new Date(startIso);
    const last = dates.length ? new Date(dates[dates.length - 1]) : start;

    const months: CalendarMonth[] = [];
    let year = start.getFullYear();
    let month = start.getMonth();
    const lastYear = last.getFullYear();
    const lastMonth = last.getMonth();

    while (year < lastYear || (year === lastYear && month <= lastMonth)) {
      months.push(this.buildCalendarMonth(year, month, highlightedDates));
      month++;
      if (month > 11) {
        month = 0;
        year++;
      }
    }

    this.calendarCache = { key, months };
    this.calendarMonthIndex = 0;
    return months;
  }
  // Index of the month currently shown in the calendar preview (0 = first month)
calendarMonthIndex = 0;

get currentCalendarMonth(): CalendarMonth | null {
  const months = this.calendarMonths; // read first so a rebuild resets the index
  if (!months.length) return null;
  return months[Math.min(this.calendarMonthIndex, months.length - 1)];
}

get canGoPrevMonth(): boolean {
  this.calendarMonths;
  return this.calendarMonthIndex > 0;
}

get canGoNextMonth(): boolean {
  return this.calendarMonthIndex < this.calendarMonths.length - 1;
}

prevMonth(): void {
  if (this.canGoPrevMonth) this.calendarMonthIndex--;
}

nextMonth(): void {
  if (this.canGoNextMonth) this.calendarMonthIndex++;
}

  // Builds one month's weekday-only grid (Mon-Fri), starting on the Monday on/before the 1st
  private buildCalendarMonth(year: number, month: number, highlighted: Set<string>): CalendarMonth {
    const firstOfMonth = new Date(year, month, 1);
    const firstOfNextMonth = new Date(year, month + 1, 1);

    const dow = firstOfMonth.getDay(); // 0=Sun..6=Sat
    const mondayOffset = dow === 0 ? -6 : 1 - dow;
    const cursor = new Date(firstOfMonth);
    cursor.setDate(firstOfMonth.getDate() + mondayOffset);

    const cells: CalendarCell[] = [];

    // Keep going until the month is covered and the last week row is complete (5 cells per row)
    while (cursor.getTime() < firstOfNextMonth.getTime() || cells.length % 5 !== 0) {
      const day = cursor.getDay();
      if (day !== 0 && day !== 6) {
        const inMonth = cursor.getMonth() === month;
        const iso = this.toISO(cursor);
        cells.push({
          day: cursor.getDate(),
          iso,
          inMonth,
          highlighted: inMonth && highlighted.has(iso)
        });
      }
      cursor.setDate(cursor.getDate() + 1);
    }

    return {
      label: firstOfMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
      cells
    };
  }

  isPreviewDate(iso: string): boolean {
    return this.previewDates.includes(iso);
  }

  // Loads the user's previously saved field selection from localStorage, if any
  private loadSelectionTemplate(): PlanSelectionTemplate | null {
    try {
      const raw = localStorage.getItem(this.SELECTION_STORAGE_KEY);
      return raw ? JSON.parse(raw) as PlanSelectionTemplate : null;
    } catch {
      return null;
    }
  }

  // Saves the current field selection to localStorage so it can prefill the next new entry.
  // If the user has switched off "Save Selection", any previously saved template is cleared instead.
  private persistSelectionTemplate(value: any): void {
    if (!this.saveSelection) {
      try {
        localStorage.removeItem(this.SELECTION_STORAGE_KEY);
      } catch {
        // Storage unavailable - nothing to do
      }

      return;
    }

    const template: PlanSelectionTemplate = {
      client: value.client,
      employee: value.employee,
      company: value.company,
      companyId: value.companyId ?? null,
      project: value.project,
      taskRef: value.taskRef,
      task: value.task,
      hours: Number(value.hours),
      hourMode: value.hourMode,
      rate: value.rate,
      allocation: value.allocation,
      charge: value.charge
    };

    try {
      localStorage.setItem(this.SELECTION_STORAGE_KEY, JSON.stringify(template));
    } catch {
      // Storage full/unavailable - silently skip; this is a
      // convenience feature, not a critical save path.
    }
  }

  // ----- Build plan entry -----

  // Builds a single PlanEntry from the current form values for the given date.
  // Only includes recurrence metadata when a seriesId is provided.
  private buildEntry(date: string, seriesId: string | null = null, id?: number): PlanEntry {
    const value = this.form.getRawValue();
    const normalizedDate = this.normalizeDate(date);

    return {
      id: id as number,
      date: normalizedDate,
      client: value.client,
      consultant: value.employee,
      entryOwner: this.authenticatedUserName,
      company: value.company,
      companyId: value.companyId ?? null,
      project: value.project,
      taskRef: value.taskRef,
      task: value.task,
      hours: Number(value.hours),
      rate: Number(value.rate?.Converted_Rate ?? value.rate),
      allocation: value.allocation,
      charge: value.charge,
      invoiceDate: this.invoiceOnSameDate
        ? normalizedDate
        : this.normalizeDate(value.invoiceDate),
      seriesId,
      isUnsaved: true,
      taskId: value.taskId ?? null,
      currencyCode: value.currencyCode ?? null,
      isClashEntry: this.data?.isClashEntry ?? false,
      recurrence: seriesId
        ? {
            pattern: value.recurrencePattern,
            dailyMode: value.dailyMode,
            dailyInterval: Number(value.dailyInterval),
            weeklyInterval: Number(value.weeklyInterval),
            weekdays: [...this.selectedWeekdays],
            occurrences: Number(value.occurrences),
            rangeMode: value.rangeMode,                                             
            endByDate: value.endByDate ? this.normalizeDate(value.endByDate) : null  
          }
        : undefined
    };
  }

  // Clears the saved selection template and resets the form to a blank new-entry state,
  // while preserving whether this dialog instance was originally opened in edit mode
  clearSelection(): void {
    try {
      localStorage.removeItem(this.SELECTION_STORAGE_KEY);
    } catch {
      // Storage unavailable - nothing to do.
    }

    const wasEditMode = this.isEditMode;
    this.data = null;
    this.buildForm();

    this.selectedWeekdays = [];
    this.rates = [];
    this.taskRefs = [];
    this.projects = [];

    this.form.get('companyId')?.setValue(null, { emitEvent: false });
    this.form.get('currencyCode')?.setValue(null, { emitEvent: false });

    this.isEditMode = wasEditMode; // preserve edit-mode framing if this was an edit dialog
  }

  // Add/Edit Plan
  addPlan(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();

    this.persistSelectionTemplate(value);

    if (value.repeatEntry) {
      if (value.recurrencePattern === 'Weekly' && this.selectedWeekdays.length === 0) {
        this.form.controls.recurrencePattern.markAsTouched();
        return;
      }

      const wasAlreadyASeries = !!this.data?.seriesId;
      const seriesId = this.data?.seriesId ?? this.generateSeriesId();
      const dates = this.generateRecurrenceDates(value.date);

      const duplicateDates = dates.filter(
        (date, index) => dates.indexOf(date) !== index
      );

      const entries = dates.map(d => this.buildEntry(d, seriesId, this.generateEntryId()));

      this.result.emit({
        entries,
        replacedSeriesId: wasAlreadyASeries ? this.data!.seriesId! : undefined,
        replacedEntryId: (this.isEditMode && !wasAlreadyASeries) ? this.data!.id : undefined
      });
    } else {
      this.result.emit({
        entries: [this.buildEntry(value.date, null, this.data?.id ?? this.generateEntryId())]
      });
    }
  }

  cancel(): void {
    this.cancelled.emit();
  }

  // Generates a unique ID to group recurring entries together as one series
  private generateSeriesId(): string {
    return (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `series-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  // Generates a temporary client-side ID for a new entry (replaced by the server-assigned ID on save)
  private generateEntryId(): number {
    return Date.now() + Math.floor(Math.random() * 1000000);
  }

}
